import React, { useState } from 'react';
import { ReactElement } from 'react';
import { TouchableOpacity, View, ViewStyle } from 'react-native';

import { SceneAction } from '@bighealth/types/src/scene-components/client';

import { Text } from 'components/sceneset-components/Text';
import { useHistory, useParams } from 'cross-platform/react-router';
import { roles } from 'cross-platform/utils/roleProps';
import { CtaCirclePlayIcon } from 'daylight/components/icons';
import { queryClientDaylightCheckin } from 'lib/api/reactQueryHelpers';
import getPathForParams from 'lib/player/getPathForParams';
import useActionHandler from 'lib/player/useActionHandler';
import * as reporter from 'lib/reporter';
import { GENERAL_ERROR_MESSAGE } from 'lib/strings';

export type RectangularPlayButtonProps = {
  action?: SceneAction;
  testID?: string;
  style?: {
    width?: number;
    height?: number;
    backgroundColor?: string;
    borderRadius?: number;
    marginBottom?: number;
  };
  text?: string;
  textColor?: string;
  iconColor?: string;
  disabled?: boolean;
  setDisabled?: (value: boolean) => void;
  onPress?: () => void;
  fontSize?: number;
  fontWeight?:
    | 'normal'
    | 'bold'
    | '100'
    | '200'
    | '300'
    | '400'
    | '500'
    | '600'
    | '700'
    | '800'
    | '900'
    | 100
    | 200
    | 300
    | 400
    | 500
    | 600
    | 700
    | 800
    | 900;
  lineHeight?: number;
  paddingVertical?: number;
  marginHorizontal?: number | string;
};

export const RectangularPlayButton = (
  props: RectangularPlayButtonProps
): ReactElement => {
  const { productReference } = useParams<{ productReference: string }>();
  const actionHandler = useActionHandler(props.action);
  const history = useHistory();
  const [playButtonDisabled, _setPlayButtonDisabled] = useState(false);

  // Just one example: right now, styles don't come from anywhere, they could be standardized or changed.
  const dimensions = {
    width: 25,
    height: 25,
    viewBox: '0 0 25 25',
  };

  const {
    disabled: isButtonDisabled,
    setDisabled: setIsButtonDisabled,
  } = props;

  const setPlayButtonDisabled = (value: boolean) => {
    setIsButtonDisabled?.(value);
    _setPlayButtonDisabled(value);
  };

  const handlePress = async (): Promise<void> => {
    if (playButtonDisabled || isButtonDisabled) {
      return;
    }

    setPlayButtonDisabled(true);

    try {
      if (props.action?.type === 'daylight/START-CHECKIN') {
        try {
          const checkinSS = await queryClientDaylightCheckin({
            product_id: props.action?.payload.productId,
            program_id: props.action?.payload.programId,
            scene_set_graph_id: props.action?.payload.sceneSetGraphId,
            scene_set_id: props.action?.payload.destinationSceneSetId,
          });

          if (checkinSS) {
            const path = getPathForParams({
              productReference: productReference,
              sceneSetGraphId: props.action?.payload.sceneSetGraphId,
              sceneSetId: props.action?.payload.destinationSceneSetId,
              sceneId: 0,
            });
            history.push(path);
          } else {
            history.push(`/${productReference}/home`);
          }
        } catch (e) {
          reporter.log(`${GENERAL_ERROR_MESSAGE} checkin failed`, e as Error);
          history.push(`/${productReference}/home`);
        }
      } else if (typeof actionHandler === 'function') {
        try {
          await actionHandler();
        } catch (e) {
          reporter.log(
            `${GENERAL_ERROR_MESSAGE} action handler failed`,
            e as Error
          );
        }
      }
    } finally {
      setPlayButtonDisabled(false);
    }
  };

  const buttonStyle: ViewStyle = {
    backgroundColor: props.style?.backgroundColor || '#000000',
    borderRadius: props.style?.borderRadius || 80,
    paddingVertical: props.paddingVertical || 16,
    marginHorizontal: (props.marginHorizontal || '5%') as any,
    borderStyle: 'solid',
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
    ...(props.style || {}),
  };

  return (
    <View>
      <TouchableOpacity
        {...roles('RectangularPlayButton', {
          labelText: props.text || 'Play lesson',
        })}
        style={buttonStyle}
        onPress={handlePress}
        accessibilityRole="button"
        disabled={playButtonDisabled || isButtonDisabled}
      >
        <Text
          style={{
            color: props.textColor || '#FFFFFF',
            fontSize: props.fontSize || 18,
            lineHeight: props.lineHeight || 24,
            fontWeight: (props.fontWeight || '500') as
              | 'normal'
              | 'bold'
              | '100'
              | '200'
              | '300'
              | '400'
              | '500'
              | '600'
              | '700'
              | '800'
              | '900',
          }}
          text={props.text || 'Play'}
        />
        {/* optional icon - can be replace */}
        <View style={{ marginLeft: 14 }}>
          <CtaCirclePlayIcon
            {...dimensions}
            circleColor={'white'}
            triangleColor={'black'}
          />
        </View>
      </TouchableOpacity>
    </View>
  );
};
